/**
 * 金币量级的整数：余额、造价、B 值、目标骰次、累计投入……**没有设计上限**（leo 2026-10-16：关卡要能一直加，
 * a0=1201 第 108 关的目标骰次约 1.6e14、造价约 1e24）。
 *
 * 口径只有一条：**从头到尾是精确整数**。
 *   - 库里是 DECIMAL(65,0)（db/schema/_shared.ts coin()），mysql2 读出来是十进制字符串
 *   - 内存里是 bigint，算式一律整数乘除、最后一步才除（四舍五入用 divRound）
 *   - 对外 JSON 是十进制字符串（字符串承载大整数），客户端不用 number 接
 * 只有「比例 / 启发式」才允许落到浮点（纠偏选点、展示用的估算骰次），钱永远不走浮点。
 *
 * 纯模块、不依赖运行时：服务端与管端预览共用。
 */

/** DECIMAL(65,0) 能装下的位数 */
export const COIN_MAX_DIGITS = 65;
/** DECIMAL(65,0) 装得下的最大值：这是列的物理容量，不是设计上限 */
export const COIN_MAX = 10n ** 65n - 1n;
/** 非负十进制整数，不带前导零，最多 65 位 */
export const COIN_RE = /^(0|[1-9][0-9]{0,64})$/;
/** 有符号版（流水的 delta） */
export const SIGNED_COIN_RE = /^(0|-?[1-9][0-9]{0,64})$/;

export const isCoinString = (v: unknown): v is string => typeof v === "string" && COIN_RE.test(v);

/**
 * 把库里读出来的值（DECIMAL 字符串）、旧快照里的 number、或已经是 bigint 的值收成 bigint。
 * number 只认安全整数——过了 2^53 的 number 已经失真，宁可报错也不按失真的数记账。
 */
export function toCoin(v: unknown, what = "金额"): bigint {
  if (typeof v === "bigint") return v;
  if (typeof v === "number") {
    if (!Number.isSafeInteger(v)) throw new RangeError(`${what} ${v} 不是精确整数`);
    return BigInt(v);
  }
  if (typeof v === "string" && /^-?[0-9]+$/.test(v)) return BigInt(v);
  throw new RangeError(`${what} ${String(v)} 不是整数`);
}

/** 校验用：读不成整数（null / 小数 / 乱码）返回 null，不抛 */
export function tryCoin(v: unknown): bigint | null {
  if (v == null) return null;
  try { return toCoin(v); } catch { return null; }
}

/** 可空列：null / undefined 原样返回 null */
export const toCoinOrNull = (v: unknown, what?: string): bigint | null => (v == null ? null : toCoin(v, what));

/** 非负整数 n ÷ d 四舍五入（.5 进位，与 Math.round 对正数的口径一致）。d 必须为正 */
export function divRound(n: bigint, d: bigint): bigint {
  if (d <= 0n) throw new RangeError("除数必须为正");
  if (n < 0n) return -divRound(-n, d);
  return (n + d / 2n) / d;
}

/** 10^k */
export const pow10 = (k: number): bigint => 10n ** BigInt(k);

/**
 * 保留 2 位有效数字的取整（round_pretty）：4285 → 4300，123456 → 120000；100 以下原样。
 * 输入是有理数 num/den（den > 0、num ≥ 0），一次取整，不经过任何中间截断。
 */
export function roundPrettyRatio(num: bigint, den: bigint): bigint {
  if (den <= 0n) throw new RangeError("除数必须为正");
  if (num < 0n) throw new RangeError("round_pretty 只接受非负数");
  const whole = num / den;
  if (whole < 100n) return divRound(num, den);
  const unit = pow10(whole.toString().length - 2);
  return divRound(num, den * unit) * unit;
}

/**
 * 算法总造价的取整（leo 2026-10-16）：≥ 100 时四舍五入到 100 的整数倍（后两位为 0），不足 100 取整到个位。
 * 输入是有理数 num/den（den > 0、num ≥ 0），一次取整
 */
export function roundHundredRatio(num: bigint, den: bigint): bigint {
  if (den <= 0n) throw new RangeError("除数必须为正");
  if (num < 0n) throw new RangeError("只接受非负数");
  return num / den < 100n ? divRound(num, den) : divRound(num, den * 100n) * 100n;
}

/** 整数版 round_pretty */
export const roundPrettyBig = (v: bigint): bigint => roundPrettyRatio(v, 1n);

/** 两个 bigint 取小 / 取大 */
export const minBig = (a: bigint, b: bigint): bigint => (a < b ? a : b);
export const maxBig = (a: bigint, b: bigint): bigint => (a > b ? a : b);

/**
 * 配置快照（管端 VersionData、对局 / 神灯命令里的老字段）里的金币量：安全整数内是 number，超出是十进制字符串。
 * 这么分是为了让存量数据的形状逐字不变（VersionData 的 checksum 吃 JSON，换成字符串会让所有老版本的 checksum 变掉），
 * 同时不给超出 2^53 的值留失真的口子。读的一侧一律 toCoin()。
 */
export type CoinValue = number | string;
const SAFE = BigInt(Number.MAX_SAFE_INTEGER);
export const coinValue = (v: bigint): CoinValue => (v >= -SAFE && v <= SAFE ? Number(v) : v.toString());
/** 库里读出来的值（DECIMAL 字符串 / number）→ CoinValue */
export const coinValueOf = (raw: unknown, what?: string): CoinValue => coinValue(toCoin(raw, what));

/**
 * bigint → number，只给「比例 / 启发式」用（纠偏选点、估算骰次）。超过 2^53 是近似值，
 * **不能**拿结果去记账、比较余额或回写库。
 */
export const approxNumber = (v: bigint): number => Number(v);
